import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  RefreshCw, 
  Plus, 
  CheckCircle2, 
  XCircle, 
  ShoppingBag,
  Settings,
  Users as UsersIcon,
  Activity,
  Radio
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [shopifyStatus, setShopifyStatus] = useState({ connected: false, lastSync: null });
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [manualRefills, setManualRefills] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchAdminData = async () => {
    try {
      const [statsRes, statusRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/stats`),
        fetch(`${BACKEND_URL}/api/admin/shopify-status`)
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
      }
      
      if (statusRes.ok) {
        const status = await statusRes.json();
        setShopifyStatus(status);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const fetchWebhookLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/webhook-logs?limit=30`);
      if (res.ok) {
        const data = await res.json();
        setWebhookLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
    fetchWebhookLogs();
    // Auto-refresh webhook logs every 8 seconds so you can ring up a POS sale and watch it land
    const interval = setInterval(fetchWebhookLogs, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleManualRefill = async () => {
    if (!customerName.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    
    if (manualRefills < 1) {
      toast.error('Number of refills must be at least 1');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/manual-refill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: customerName.trim(),
          refillCount: parseInt(manualRefills)
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${manualRefills} refill${manualRefills > 1 ? 's' : ''} for ${customerName}`);
        setCustomerName('');
        setManualRefills(1);
        fetchAdminData();
      } else {
        toast.error('Failed to add refill');
      }
    } catch (error) {
      toast.error('Error adding refill');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncShopify = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sync-shopify`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Shopify sync initiated');
        fetchAdminData();
      } else {
        toast.error('Failed to sync with Shopify');
      }
    } catch (error) {
      toast.error('Error syncing with Shopify');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeRealData = async () => {
    if (!window.confirm('This will replace ALL current data with your real business numbers (2,436 refills, 200 customers, 90,132 bottles saved). Continue?')) {
      return;
    }
    
    setLoading(true);
    toast.loading('Initializing real data... This may take 30 seconds...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/initialize-real-data`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Success! Initialized ${data.stats.totalRefills.toLocaleString()} refills and ${data.stats.totalBottlesSaved.toLocaleString()} bottles saved!`);
        setTimeout(() => {
          fetchAdminData();
          window.location.href = '/';
        }, 2000);
      } else {
        toast.error('Failed to initialize data');
      }
    } catch (error) {
      toast.error('Error initializing data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/50">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-muted"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Admin Dashboard
                </h1>
                <p className="text-sm text-muted-foreground">Manage refills and Shopify integration</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <Activity className="w-4 h-4" />
              View Public Display
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Bottles Saved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                {stats?.totalBottlesSaved.toLocaleString() || '0'}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Refills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-secondary">
                {stats?.totalRefills.toLocaleString() || '0'}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Customers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-accent">
                {stats?.totalCustomers.toLocaleString() || '0'}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">This Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-accent">
                {stats?.thisMonth.toLocaleString() || '0'}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Shopify Integration */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-primary" />
                    Shopify Integration
                  </CardTitle>
                  <CardDescription className="mt-1">Connect your Shopify store for automatic tracking</CardDescription>
                </div>
                {shopifyStatus.connected ? (
                  <Badge variant="outline" className="gap-1 bg-success/10 text-success border-success/30">
                    <CheckCircle2 className="w-3 h-3" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 bg-muted text-muted-foreground">
                    <XCircle className="w-3 h-3" />
                    Not Connected
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <h4 className="text-sm font-semibold text-foreground mb-2">Webhook URL</h4>
                <code className="text-xs bg-card px-2 py-1 rounded border border-border block overflow-x-auto">
                  {BACKEND_URL}/api/shopify/webhook
                </code>
                <p className="text-xs text-muted-foreground mt-2">
                  Configure this URL in your Shopify admin under Settings → Notifications → Webhooks
                </p>
              </div>
              
              {shopifyStatus.lastSync && (
                <div className="text-sm text-muted-foreground">
                  Last sync: {new Date(shopifyStatus.lastSync).toLocaleString()}
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={handleSyncShopify}
                  disabled={loading}
                  className="flex-1 gap-2"
                  variant="outline"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Sync Now
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => window.open('https://admin.shopify.com', '_blank')}
                >
                  <Settings className="w-4 h-4" />
                  Shopify Admin
                </Button>
              </div>
              
              <div className="mt-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-accent">Note:</strong> This is a prototype with mock Shopify integration. 
                  In production, configure your Shopify webhook to POST order data to the endpoint above.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Manual Entry */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                Manual Refill Entry
              </CardTitle>
              <CardDescription className="mt-1">Add refills manually for walk-in customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="customerName">Customer Name</Label>
                  <Input
                    id="customerName"
                    placeholder="Enter customer name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="refillCount">Number of Refills</Label>
                  <Input
                    id="refillCount"
                    type="number"
                    min="1"
                    value={manualRefills}
                    onChange={(e) => setManualRefills(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Each refill saves approximately 37 plastic bottles
                  </p>
                </div>
              </div>

              <Separator />

              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Impact Preview</span>
                  <Badge variant="outline" className="bg-accent/10 text-accent border-accent/30">
                    {manualRefills} Refill{manualRefills > 1 ? 's' : ''}
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-primary">
                  {(manualRefills * 37).toLocaleString()} bottles
                </div>
                <p className="text-xs text-muted-foreground mt-1">will be prevented from landfills</p>
              </div>

              <Button
                onClick={handleManualRefill}
                disabled={loading || !customerName.trim()}
                className="w-full gap-2 bg-primary hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Add Refill
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Initialize Real Data Section */}
        <Card className="mt-8 border-2 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Activity className="w-5 h-5" />
              Initialize Real Business Data
            </CardTitle>
            <CardDescription className="mt-1">
              One-time setup: Load your actual business numbers into the tracker
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h4 className="font-semibold text-foreground mb-3">This will initialize your tracker with:</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                  <strong className="text-foreground">2,436</strong> total refills since August 9th, 2024
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                  <strong className="text-foreground">90,132</strong> plastic bottles prevented from landfills
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                  <strong className="text-foreground">200</strong> customers tracked
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                  <strong className="text-foreground">561</strong> December refills
                </li>
              </ul>
            </div>
            
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-700 dark:text-yellow-500">
                <strong>⚠️ Warning:</strong> This will replace all current data. Only click this button once when setting up your tracker.
              </p>
            </div>

            <Button
              onClick={handleInitializeRealData}
              disabled={loading}
              className="w-full gap-2 bg-primary hover:bg-primary/90"
              size="lg"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Initialize Real Data Now
            </Button>
          </CardContent>
        </Card>

        {/* Live Webhook Activity */}
        <Card className="mt-8" data-testid="webhook-activity-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Radio className="w-5 h-5 text-primary" />
                  Live Webhook Activity
                </CardTitle>
                <CardDescription className="mt-1">
                  Every order Shopify sends us appears here in real time — online and POS. Ring up an in-store sale and watch it land below within ~5 seconds.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchWebhookLogs}
                disabled={logsLoading}
                className="gap-2"
                data-testid="refresh-webhook-logs-btn"
              >
                <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {webhookLogs.length === 0 ? (
              <div className="p-8 text-center bg-muted/40 rounded-lg border border-dashed border-border">
                <p className="text-sm text-muted-foreground" data-testid="webhook-logs-empty">
                  No webhook events received yet. After you ring up a POS or online sale, it should appear here within seconds.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  If a sale completes but nothing shows up here, the issue is in Shopify (webhook not subscribed for POS) — not the tracker.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="webhook-logs-list">
                {webhookLogs.map((log, idx) => {
                  const channelLabel =
                    log.sourceName === 'pos' ? 'POS (In-Store)' :
                    log.sourceName === 'web' ? 'Online Store' :
                    log.sourceName || 'Unknown';
                  const channelColor =
                    log.sourceName === 'pos' ? 'bg-accent/10 text-accent border-accent/30' :
                    log.sourceName === 'web' ? 'bg-primary/10 text-primary border-primary/30' :
                    'bg-muted text-muted-foreground';
                  return (
                    <div
                      key={idx}
                      className="p-3 border border-border rounded-lg bg-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                      data-testid={`webhook-log-row-${idx}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge variant="outline" className={channelColor}>
                            {channelLabel}
                          </Badge>
                          {log.matched ? (
                            <Badge variant="outline" className="bg-success/10 text-success border-success/30 gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Counted ({log.refillsCounted} refill{log.refillsCounted !== 1 ? 's' : ''})
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-muted text-muted-foreground gap-1">
                              <XCircle className="w-3 h-3" />
                              Not a refill
                            </Badge>
                          )}
                          {log.orderName && (
                            <span className="text-xs text-muted-foreground">{log.orderName}</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {(log.lineItemTitles || []).map(li => `${li.title} ×${li.quantity}`).join(', ') || '—'}
                        </div>
                        {log.error && (
                          <div className="text-xs text-destructive mt-1">Error: {log.error}</div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground shrink-0">
                        {log.receivedAt ? new Date(log.receivedAt).toLocaleString() : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Quick Setup Guide</CardTitle>
            <CardDescription>Follow these steps to integrate with your Shopify store</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                  1
                </span>
                <span>Log in to your Shopify Admin panel</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                  2
                </span>
                <span>Navigate to Settings → Notifications → Webhooks</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                  3
                </span>
                <span>Create a new webhook for "Order creation" event</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                  4
                </span>
                <span>Paste the webhook URL shown above in the URL field</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                  5
                </span>
                <span>Set the format to JSON and save the webhook</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs font-semibold">
                  6
                </span>
                <span><strong className="text-foreground">For POS sales:</strong> the same "Order creation" webhook fires for in-store orders. Ring up a real POS sale, then watch the "Live Webhook Activity" panel — if it appears, you're set; if it doesn't, check Shopify Admin → Settings → Notifications → Webhooks → Recent Deliveries to see what Shopify did (or didn't) send.</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
